import { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

const BATCH_SIZE = 30;

// ─── Parsed row type ──────────────────────────────────────────────────────────
interface StockRow {
  productCode: string;       // Stok Kodu / Malzeme Kodu
  name: string;              // Stok Adı / Malzeme Tanımı
  barcode: string;
  category: string;          // Grubu / alt grup
  brand: string;
  model: string;
  stock: number;             // Kalan Miktar / Servis Mevcut Stok
  unit: string;              // Birimi
  salePrice: number;         // Satış Fiyatı 1
  purchasePrice: number;
  shelf: string;             // Raf No
  sourceFormat: "akinsoft" | "servis" | "unknown";
}

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// ─── Format detection & parsing ───────────────────────────────────────────────
function detectAndParse(rawRows: unknown[][]): StockRow[] {
  if (!rawRows || rawRows.length === 0) throw new Error("Excel dosyası boş.");

  // Find header row
  let headerIdx = -1;
  let format: "akinsoft" | "servis" | "unknown" = "unknown";

  for (let i = 0; i < Math.min(rawRows.length, 10); i++) {
    const row = rawRows[i];
    if (!row) continue;
    const cells = row.map((c) => String(c ?? "").trim());
    const joined = cells.join("|");

    if (joined.includes("Stok Kodu") && joined.includes("Stok Adı")) {
      headerIdx = i;
      format = "akinsoft";
      break;
    }
    if (joined.includes("Malzeme Kodu") && joined.includes("Malzeme Tanımı")) {
      headerIdx = i;
      format = "servis";
      break;
    }
  }

  if (headerIdx === -1) {
    // Try first row as header fallback
    const firstRow = rawRows[0]?.map((c) => String(c ?? "").trim()) ?? [];
    if (firstRow.some((c) => c.toLowerCase().includes("kod") || c.toLowerCase().includes("stok"))) {
      headerIdx = 0;
      format = "unknown";
    } else {
      throw new Error(
        "Excel formatı tanınamadı. 'Stok Kodu'/'Stok Adı' (akınsoft) veya 'Malzeme Kodu'/'Malzeme Tanımı' (Servis Stok Raporu) içeren sütun başlıkları bekleniyordu."
      );
    }
  }

  const headers = (rawRows[headerIdx] ?? []).map((c) => String(c ?? "").trim());

  const col = (name: string) => headers.findIndex((h) => h === name || h.toLowerCase().includes(name.toLowerCase()));

  // Column index mapping per format
  const idxCode  = format === "akinsoft" ? col("Stok Kodu")       : col("Malzeme Kodu");
  const idxName  = format === "akinsoft" ? col("Stok Adı")        : col("Malzeme Tanımı");
  const idxStock = format === "akinsoft" ? col("Kalan Miktar")    : col("Servis Mevcut Stok");
  const idxUnit  = format === "akinsoft" ? col("Birimi")          : -1;
  const idxPrice = format === "akinsoft" ? col("Satış Fiyatı 1")  : -1;
  const idxShelf = format === "akinsoft" ? col("Raf No")          : -1;
  const idxCat   = format === "akinsoft" ? col("Grubu")           : -1;
  const idxBrand = format === "akinsoft" ? col("Marka")           : -1;
  const idxModel = format === "akinsoft" ? col("Model")           : -1;
  const idxBarc  = format === "akinsoft" ? col("Barkodu")         : -1;

  const toNum = (v: unknown): number => {
    if (v === null || v === undefined || v === "" || String(v) === "NaN") return 0;
    const n = parseFloat(String(v).replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  const result: StockRow[] = [];

  for (let i = headerIdx + 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    if (!row) continue;

    const code = String(row[idxCode] ?? "").trim();
    const name = String(row[idxName] ?? "").trim();
    if (!name && !code) continue;         // completely empty row
    if (name === "NaN" && code === "NaN") continue;

    result.push({
      productCode:   code,
      name:          name || code,
      barcode:       idxBarc >= 0 ? String(row[idxBarc] ?? "").trim() : "",
      category:      idxCat  >= 0 ? String(row[idxCat]  ?? "").trim() : "",
      brand:         idxBrand >= 0 ? String(row[idxBrand] ?? "").trim() : "",
      model:         idxModel >= 0 ? String(row[idxModel] ?? "").trim() : "",
      stock:         idxStock >= 0 ? toNum(row[idxStock]) : 0,
      unit:          idxUnit  >= 0 ? String(row[idxUnit]  ?? "").trim() : "ADET",
      salePrice:     idxPrice >= 0 ? toNum(row[idxPrice]) : 0,
      purchasePrice: 0,
      shelf:         idxShelf >= 0 ? String(row[idxShelf] ?? "").trim() : "",
      sourceFormat:  format,
    });
  }

  if (result.length === 0) {
    throw new Error("Excel dosyasında işlenebilir kayıt bulunamadı.");
  }

  return result;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function StockImportPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId || user?.id || "";

  const [step, setStep]         = useState<"upload" | "preview" | "progress" | "done" | "error">("upload");
  const [file, setFile]         = useState<File | null>(null);
  const [rows, setRows]         = useState<StockRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [result, setResult]     = useState<ImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── File processing ────────────────────────────────────────────────────────
  const processFile = async (f: File) => {
    setErrorMsg("");
    try {
      const XLSX = await import("xlsx");
      const buf  = await f.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][];
      const parsed = detectAndParse(raw);
      setRows(parsed);
      setStep("preview");
    } catch (err: any) {
      setErrorMsg(err.message || "Dosya okunurken hata oluştu.");
      setStep("error");
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    await processFile(f);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    setFile(f);
    await processFile(f);
  };

  // ── Import ─────────────────────────────────────────────────────────────────
  const startImport = async () => {
    if (!rows.length || !dealerId) return;
    setStep("progress");
    setProgress(0);
    const supabase = getSupabase();

    const res: ImportResult = { created: 0, updated: 0, skipped: 0, errors: [] };

    try {
      // 1. Ensure all categories exist → build catMap
      const catNames = [...new Set(rows.map((r) => r.category).filter(Boolean))];
      const catMap: Record<string, string> = {};

      const { data: existingCats } = await supabase
        .from("categories")
        .select("id, name")
        .eq("dealer_id", dealerId);

      for (const c of existingCats ?? []) catMap[c.name] = c.id;

      for (const catName of catNames) {
        if (!catMap[catName]) {
          const { data: nc, error: ce } = await supabase
            .from("categories")
            .insert({ dealer_id: dealerId, name: catName })
            .select("id, name")
            .single();
          if (ce) {
            res.errors.push(`Kategori oluşturulamadı: ${catName}`);
          } else if (nc) {
            catMap[nc.name] = nc.id;
          }
        }
      }

      // 2. Fetch existing products for this dealer (by product_code) for upsert logic
      const { data: existingProds } = await supabase
        .from("products")
        .select("id, product_code, barcode")
        .eq("dealer_id", dealerId);

      const codeToId: Record<string, string> = {};
      const barcodeToId: Record<string, string> = {};
      for (const p of existingProds ?? []) {
        if (p.product_code) codeToId[String(p.product_code).trim()]  = p.id;
        if (p.barcode)      barcodeToId[String(p.barcode).trim()]    = p.id;
      }

      // 3. Process rows in batches
      const total = rows.length;

      for (let i = 0; i < total; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);

        for (const row of batch) {
          const existingId =
            (row.productCode && codeToId[row.productCode]) ||
            (row.barcode     && barcodeToId[row.barcode])  ||
            null;

          const payload: Record<string, unknown> = {
            dealer_id:      dealerId,
            name:           row.name,
            product_code:   row.productCode || null,
            barcode:        row.barcode     || null,
            category_id:    catMap[row.category] || null,
            brand:          row.brand  || null,
            model:          row.model  || null,
            stock:          row.stock,
            sale_price:     row.salePrice,
            purchase_price: row.purchasePrice,
            shelf_location: row.shelf  || null,
          };

          if (existingId) {
            // UPDATE — only update stock and fields that may have changed
            const { error: ue } = await supabase
              .from("products")
              .update({ stock: row.stock, sale_price: row.salePrice, shelf_location: row.shelf || null })
              .eq("id", existingId);
            if (ue) {
              res.errors.push(`Güncellenemedi: ${row.name} (${ue.message})`);
              res.skipped++;
            } else {
              res.updated++;
            }
          } else {
            // INSERT
            const { error: ie } = await supabase.from("products").insert(payload);
            if (ie) {
              res.errors.push(`Eklenemedi: ${row.name} (${ie.message})`);
              res.skipped++;
            } else {
              res.created++;
              // Register new code/barcode in our lookup so duplicates in the same file are caught
              if (row.productCode) codeToId[row.productCode]  = "new";
              if (row.barcode)     barcodeToId[row.barcode]   = "new";
            }
          }
        }

        setProgress(Math.round(((i + batch.length) / total) * 100));
        await new Promise((r) => setTimeout(r, 20));
      }

      setResult(res);
      setStep("done");
      logAction(
        dealerId,
        user?.id || null,
        LOG_ACTIONS.IMPORT,
        `Stok içe aktarımı: ${res.created} eklendi, ${res.updated} güncellendi, ${res.skipped} atlandı · Dosya: ${file?.name ?? "-"}`
      );
    } catch (err: any) {
      setErrorMsg(err.message || "Aktarım sırasında beklenmedik hata oluştu.");
      setStep("error");
    }
  };

  const reset = () => {
    setStep("upload");
    setFile(null);
    setRows([]);
    setProgress(0);
    setResult(null);
    setErrorMsg("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatLabel: Record<string, string> = {
    akinsoft: "akınsoft Stok Listesi",
    servis:   "Servis Stok Bakiye Raporu",
    unknown:  "Genel Format",
  };

  const detectedFormat = rows[0]?.sourceFormat ?? null;

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground-950">Excel'den Stok Yükleme</h4>
        <p className="text-sm text-foreground-500 mt-0.5">
          akınsoft veya Servis Stok Bakiye Raporu formatındaki Excel dosyalarını otomatik olarak tanır.
          Sistemde olmayan ürünler eklenir, mevcut olanların stok bilgisi güncellenir.
        </p>
      </div>

      {/* Supported formats info */}
      {step === "upload" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {[
            {
              icon: "ri-file-excel-2-line",
              color: "bg-emerald-50 border-emerald-200 text-emerald-700",
              title: "akınsoft Stok Listesi",
              cols: "Stok Kodu, Stok Adı, Kalan Miktar, Satış Fiyatı, Grubu, Raf No",
            },
            {
              icon: "ri-stack-line",
              color: "bg-blue-50 border-blue-200 text-blue-700",
              title: "Servis Stok Bakiye Raporu",
              cols: "Malzeme Kodu, Malzeme Tanımı, Servis Mevcut Stok, Toplam",
            },
          ].map((f) => (
            <div key={f.title} className={`flex items-start gap-3 p-3 rounded-xl border ${f.color}`}>
              <i className={`${f.icon} text-xl mt-0.5`}></i>
              <div>
                <p className="text-sm font-semibold">{f.title}</p>
                <p className="text-xs opacity-80 mt-0.5">{f.cols}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">

        {/* ── UPLOAD ───────────────────────────────────────────────────────── */}
        {(step === "upload" || step === "error") && (
          <div className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-primary-400 bg-primary-50"
                  : "border-background-300 hover:border-primary-300 hover:bg-background-100/50"
              }`}
            >
              <i className="ri-upload-cloud-2-line text-4xl text-foreground-400 mb-3 block"></i>
              <p className="text-sm font-medium text-foreground-700">Excel dosyasını sürükleyip bırakın veya seçin</p>
              <p className="text-xs text-foreground-400 mt-1">.xlsx, .xls formatları desteklenir</p>
              <button className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer">
                <i className="ri-folder-open-line"></i> Dosya Seç
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {errorMsg && (
              <div className="p-4 bg-accent-50 border border-accent-200 rounded-xl text-sm text-accent-700 flex items-start gap-2">
                <i className="ri-error-warning-line text-lg shrink-0"></i>
                <div>
                  <p className="font-medium mb-1">Dosya okunamadı</p>
                  <p>{errorMsg}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── PREVIEW ──────────────────────────────────────────────────────── */}
        {step === "preview" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="font-semibold text-foreground-950">Önizleme</h5>
                <p className="text-xs text-foreground-500 mt-0.5">
                  <span className="font-medium">{file?.name}</span> ·{" "}
                  {detectedFormat && (
                    <span className="text-primary-600 font-medium">{formatLabel[detectedFormat]} </span>
                  )}
                  — {rows.length} kayıt okundu
                </p>
              </div>
              <button onClick={reset} className="text-sm text-foreground-400 hover:text-foreground-600 flex items-center gap-1 cursor-pointer">
                <i className="ri-close-line"></i> İptal
              </button>
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "Toplam Kayıt",    val: rows.length,                                  color: "text-foreground-900"  },
                { label: "Stoklu Ürün",     val: rows.filter((r) => r.stock > 0).length,       color: "text-emerald-700"     },
                { label: "Kategorisiz",     val: rows.filter((r) => !r.category).length,       color: "text-amber-700"       },
              ].map((s) => (
                <div key={s.label} className="bg-background-100 rounded-xl p-3 text-center">
                  <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                  <p className="text-xs text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Table preview — first 50 rows */}
            <div className="border border-background-200/70 rounded-xl overflow-hidden mb-4">
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-xs min-w-[700px]">
                  <thead className="bg-background-100 sticky top-0">
                    <tr>
                      {["#", "Ürün Kodu", "Ürün Adı", "Kategori", "Stok", "Birim", "Satış Fiyatı", "Raf"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-medium text-foreground-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className="border-t border-background-200/70 hover:bg-background-100/40">
                        <td className="px-3 py-2 text-foreground-400">{idx + 1}</td>
                        <td className="px-3 py-2 text-foreground-600 font-mono">{row.productCode || "-"}</td>
                        <td className="px-3 py-2 text-foreground-900 font-medium max-w-[220px] truncate">{row.name}</td>
                        <td className="px-3 py-2 text-foreground-500">{row.category || <span className="text-amber-500">—</span>}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={row.stock > 0 ? "text-emerald-700 font-semibold" : "text-foreground-400"}>{row.stock}</span>
                        </td>
                        <td className="px-3 py-2 text-foreground-500">{row.unit}</td>
                        <td className="px-3 py-2 text-right text-foreground-600">
                          {row.salePrice > 0 ? `₺${row.salePrice.toFixed(2)}` : "-"}
                        </td>
                        <td className="px-3 py-2 text-foreground-400">{row.shelf || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 50 && (
                <div className="px-4 py-2 bg-background-100 text-xs text-foreground-400 border-t border-background-200/70">
                  İlk 50 satır gösteriliyor — toplam {rows.length} kayıt aktarılacak
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={startImport}
                className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-upload-cloud-2-line"></i>
                {rows.length} Kaydı Sisteme Aktar
              </button>
              <button
                onClick={reset}
                className="px-6 py-3 bg-background-100 border border-background-200/70 text-foreground-600 rounded-xl text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                Vazgeç
              </button>
            </div>
          </div>
        )}

        {/* ── PROGRESS ─────────────────────────────────────────────────────── */}
        {step === "progress" && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-loader-4-line text-2xl text-primary-600 animate-spin"></i>
            </div>
            <p className="font-semibold text-foreground-950 mb-1">Stok verileri yükleniyor...</p>
            <p className="text-xs text-foreground-500 mb-6">Lütfen bekleyin, sayfayı kapatmayın</p>
            <div className="max-w-sm mx-auto">
              <div className="flex items-center justify-between text-xs text-foreground-500 mb-1.5">
                <span>İlerleme</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <div className="w-full bg-background-200 rounded-full h-2.5">
                <div
                  className="bg-primary-500 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── DONE ─────────────────────────────────────────────────────────── */}
        {step === "done" && result && (
          <div className="py-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-fill text-2xl text-emerald-500"></i>
              </div>
              <div>
                <h5 className="font-semibold text-foreground-950">Aktarım Tamamlandı</h5>
                <p className="text-sm text-foreground-500">{file?.name}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "Yeni Eklenen",  val: result.created,  icon: "ri-add-circle-line",    color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
                { label: "Güncellenen",   val: result.updated,  icon: "ri-refresh-line",        color: "text-blue-700 bg-blue-50 border-blue-200"           },
                { label: "Atlanan",       val: result.skipped,  icon: "ri-skip-forward-line",   color: "text-amber-700 bg-amber-50 border-amber-200"        },
              ].map((s) => (
                <div key={s.label} className={`rounded-xl p-4 border text-center ${s.color}`}>
                  <i className={`${s.icon} text-2xl block mb-1`}></i>
                  <p className="text-2xl font-bold">{s.val}</p>
                  <p className="text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {result.errors.length > 0 && (
              <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-xl">
                <p className="text-xs font-semibold text-accent-700 mb-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {result.errors.length} hata oluştu:
                </p>
                <ul className="space-y-1 max-h-36 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-xs text-accent-600 flex items-start gap-1">
                      <i className="ri-close-circle-line shrink-0 mt-0.5"></i>
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={reset}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-upload-cloud-2-line"></i> Yeni Dosya Yükle
              </button>
              <button
                onClick={() => window.location.href = "/products"}
                className="px-6 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-xl text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                Ürünlere Git
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
